import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { type ListResponse } from '@zag/core/models';
import { type WebhookEndpointResponse, type WebhookDeliveryLogResponse } from '@zag/notifications/common';

export interface CreateWebhookEndpointRequest {
  name: string;
  url: string;
  secret?: string;
  event_types: string[];
  is_active?: boolean;
  custom_headers?: Record<string, string> | null;
  retry_policy?: {
    maxAttempts?: number;
    backoffType?: 'exponential' | 'fixed';
    initialDelayMs?: number;
    maxDelayMs?: number;
    backoffMultiplier?: number;
  } | null;
}

export interface UpdateWebhookEndpointRequest {
  name?: string;
  url?: string;
  event_types?: string[];
  is_active?: boolean;
  custom_headers?: Record<string, string> | null;
  retry_policy?: {
    maxAttempts?: number;
    backoffType?: 'exponential' | 'fixed';
    initialDelayMs?: number;
    maxDelayMs?: number;
    backoffMultiplier?: number;
  } | null;
}

/** Full endpoint response including the secret (only returned on create) */
export interface WebhookEndpointCreatedResponse extends WebhookEndpointResponse {
  secret: string;
}

@Injectable({ providedIn: 'root' })
export class WebhookEndpointService {
  private readonly http = inject(HttpClient);

  /** Pagination signal driving the reactive list */
  readonly listPagination = signal<{ take: number; skip: number }>({ take: 20, skip: 0 });

  /** Reactive webhook endpoint list */
  readonly listResource = rxResource({
    params: () => this.listPagination(),
    stream: ({ params }) =>
      this.http.get<ListResponse<WebhookEndpointResponse>>('/api/v1/notifications/webhooks', {
        params: {
          take: String(params.take),
          skip: String(params.skip),
        },
      }),
  });

  getWebhookEndpoint(id: string): Observable<WebhookEndpointResponse> {
    return this.http.get<WebhookEndpointResponse>(`/api/v1/notifications/webhooks/${id}`);
  }

  createWebhookEndpoint(data: CreateWebhookEndpointRequest): Observable<WebhookEndpointCreatedResponse> {
    return this.http.post<WebhookEndpointCreatedResponse>('/api/v1/notifications/webhooks', data);
  }

  updateWebhookEndpoint(id: string, data: UpdateWebhookEndpointRequest): Observable<WebhookEndpointResponse> {
    return this.http.put<WebhookEndpointResponse>(`/api/v1/notifications/webhooks/${id}`, data);
  }

  deleteWebhookEndpoint(id: string): Observable<void> {
    return this.http.delete<void>(`/api/v1/notifications/webhooks/${id}`);
  }

  testWebhookEndpoint(id: string): Observable<{ endpointId: string; eventId: string; deliveryIds: string[]; status: string }> {
    return this.http.post<{ endpointId: string; eventId: string; deliveryIds: string[]; status: string }>(
      `/api/v1/notifications/webhooks/${id}/delivery-tests`,
      {}
    );
  }

  getDeliveryLogs(id: string, skip = 0, take = 20): Observable<ListResponse<WebhookDeliveryLogResponse>> {
    return this.http.get<ListResponse<WebhookDeliveryLogResponse>>(`/api/v1/notifications/webhooks/${id}/deliveries`, {
      params: { take: String(take), skip: String(skip) },
    });
  }
}
